import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import CreatableSelect from 'react-select/creatable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faDownload } from '@fortawesome/pro-light-svg-icons';
import { webinars as webinarUtils } from '@shopmy/universal';
import { createWebinar, updateWebinar, fetchWebinarTopics } from '../../api/webinars';
import { WEBINAR_STOCK_BACKGROUNDS } from '../../constants/webinar_constants';
import { getSmartImage } from '../../helpers/helpers';
import WebinarZoomAssets, { cardMissingFields, exportCardPng } from './WebinarZoomAssets';
import ImageUploader from '../../ImageUpload/ImageUploader';
import { uploadFileToS3 } from '../../ImageUpload/uploadFileToS3';
import ShopmyImage from '../General/ShopmyImage';
import './Webinars.scss';

const getNyOffset = dateStr => {
  const probe = new Date(`${dateStr || moment().format('YYYY-MM-DD')}T12:00:00Z`);
  const tz =
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      timeZoneName: 'longOffset'
    })
      .formatToParts(probe)
      .find(part => part.type === 'timeZoneName')?.value || 'GMT-04:00';
  const match = tz.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/);
  if (!match) return '-04:00';
  return `${match[1]}${match[2].padStart(2, '0')}:${(match[3] || '00').padStart(2, '0')}`;
};

const toEtParts = utc => {
  if (!utc) return { date: '', time: '' };
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(new Date(utc));
  const get = type => parts.find(part => part.type === type)?.value || '';
  return { date: `${get('year')}-${get('month')}-${get('day')}`, time: `${get('hour')}:${get('minute')}` };
};

const toDateInput = utc => toEtParts(utc).date;
const toTimeInput = utc => toEtParts(utc).time;
const combineToUtc = (date, time) => {
  if (!date) return null;
  return moment(`${date}T${time || '12:00'}${getNyOffset(date)}`).utc().toISOString();
};

const getEasternOffsetLabel = dateStr => {
  const tz =
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      timeZoneName: 'longOffset'
    })
      .formatToParts(new Date(`${dateStr || moment().format('YYYY-MM-DD')}T12:00:00Z`))
      .find(part => part.type === 'timeZoneName')?.value || 'GMT-04:00';
  return `(${tz}) Eastern time (US and Canada)`;
};

// Duration is stored as seconds; admins type and read the familiar "58:12" form.
const formatDurationSeconds = total => {
  if (!Number.isFinite(Number(total)) || Number(total) <= 0) return '';
  const t = Number(total);
  const pad = n => String(n).padStart(2, '0');
  const hours = Math.floor(t / 3600);
  const minutes = Math.floor((t % 3600) / 60);
  const seconds = t % 60;
  return hours ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
};

const parseDurationInput = text => {
  const parts = (text || '').trim().split(':').map(Number);
  if (!parts.length || parts.some(n => !Number.isFinite(n) || n < 0)) return null;
  const seconds = parts.reduce((sum, part) => sum * 60 + part, 0);
  return seconds > 0 ? seconds : null;
};

const formFromWebinar = webinar => ({
  title: webinar?.title || '',
  slug: webinar?.slug || '',
  description: webinar?.description || '',
  topic: webinar?.topic || '',
  heroImage: webinar?.heroImage || '',
  date: toDateInput(webinar?.startsAt),
  time: toTimeInput(webinar?.startsAt),
  registrationUrl: webinar?.registrationUrl || '',
  replayUrl: webinar?.replayUrl || '',
  replayKind: webinar?.replayKind || 'hosted',
  replayPasscode: webinar?.replayPasscode || '',
  replayDuration: formatDurationSeconds(webinar?.durationSeconds),
  guestSpeaker: webinar?.speakers?.[0]?.name || '',
  handle: webinar?.speakers?.[0]?.handle || '',
  tier: webinar?.speakers?.[0]?.tier || '',
  // Cropped uploads come back as private S3 URLs, which only render through the CloudFront alias.
  headshot: getSmartImage(webinar?.speakers?.[0]?.headshot) || '',
  headshotSource: webinar?.speakers?.[0]?.headshotSource || '',
  backgroundImage: webinar?.backgroundImage || '',
  keyTakeaways: (webinar?.keyTakeaways || []).map(toTakeawayFields)
});

const { isTakeawayOverLimit, KEY_TAKEAWAY_MAX, KEY_TAKEAWAY_MAX_CHARS, EVENT_OVERVIEW_MAX_CHARS } = webinarUtils;
const LONG_NAME_CHARS = 22;
const SPEAKER_TIERS = ['Icon', 'Trendsetter', 'Ambassador', 'Enthusiast'];

const KEY_TAKEAWAY_LEAD_MAX_CHARS = 60;
// The Event Overview export draws the headshot at 1254px, so the crop must save well above the 600px default.
const HEADSHOT_CROP_SIZE = 1600;

// The API already normalizes each takeaway to a plain line or { lead, copy } with the first words set bold.
const toTakeawayFields = item => (typeof item === 'string' ? { lead: '', copy: item } : { lead: item.lead || '', copy: item.copy || '' });

const toSavedTakeaway = ({ lead, copy }) => {
  const trimmedLead = lead.trim();
  const trimmedCopy = copy.trim();
  if (!trimmedLead && !trimmedCopy) return null;
  return trimmedLead ? { lead: trimmedLead, copy: trimmedCopy } : trimmedCopy;
};

const downloadFile = (blob, filename) => {
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(href), 60 * 1000);
};

const IMAGE_SLOTS = [
  {
    field: 'headshot',
    title: 'Add Guest Headshot',
    helper: 'Shown in the circular frame on the Zoom card and on the Event Overview image',
    sizeHint: 'Recommended: square, at least 800 × 800px'
  }
];

const WebinarEditor = ({ webinar, mode, onSave, onCancel, onDelete }) => {
  const isUpcoming = mode === 'upcoming';
  const [form, setForm] = useState(formFromWebinar(webinar));
  const [topicOptions, setTopicOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [imageUploaderField, setImageUploaderField] = useState(null);
  const [missingStockIds, setMissingStockIds] = useState({});
  const hasBeenPublished = !!webinar?.publishedAt;
  const [visibility, setVisibility] = useState(webinar?.status === 'draft' ? 'published' : webinar?.status || 'published');

  useEffect(() => {
    fetchWebinarTopics()
      .then(topics => setTopicOptions((topics || []).map(t => ({ value: t, label: t }))))
      .catch(() => {});
  }, []);

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const setImageField = (field, url, sourceUrl) => setForm(prev => ({ ...prev, [field]: getSmartImage(url) || '', [`${field}Source`]: sourceUrl || '' }));

  const downloadStockBackground = async (e, stock) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const response = await fetch(stock.url);
      if (!response.ok) throw new Error(response.statusText);
      downloadFile(await response.blob(), `webinar-background-${stock.id}.jpg`);
    } catch {
      window.ALERT.error('Failed to download the stock background.');
    }
  };

  const takeaways = form.keyTakeaways;
  const setTakeawayField = (index, key, value) =>
    setForm(prev => ({
      ...prev,
      keyTakeaways: prev.keyTakeaways.map((item, i) => (i === index ? { ...item, [key]: value } : item))
    }));
  const addTakeaway = () => {
    if (takeaways.length >= KEY_TAKEAWAY_MAX) return;
    setForm(prev => ({ ...prev, keyTakeaways: [...prev.keyTakeaways, { lead: '', copy: '' }] }));
  };
  const removeTakeaway = index => setForm(prev => ({ ...prev, keyTakeaways: prev.keyTakeaways.filter((item, i) => i !== index) }));

  const zoomAssetInput = {
    slug: form.slug.trim(),
    title: form.title.trim(),
    description: form.description.trim(),
    backgroundImage: form.backgroundImage.trim(),
    speakers: [{ name: form.guestSpeaker.trim(), handle: form.handle.trim(), tier: form.tier, headshot: form.headshot.trim() }],
    keyTakeaways: form.keyTakeaways.map(({ lead, copy }) => ({ lead: lead.trim(), copy: copy.trim() }))
  };

  const uploadCardArt = async () => {
    try {
      const png = await exportCardPng(zoomAssetInput.slug);
      return await uploadFileToS3(new File([png], `${zoomAssetInput.slug || 'webinar'}-card.png`, { type: 'image/png' }));
    } catch {
      window.ALERT.warn('The card image could not be made. Link previews will show the background photo until the next save.');
      return null;
    }
  };

  const save = async (status, overrides = {}) => {
    if (!form.title.trim()) {
      setError('Event name is required.');
      return;
    }
    const registrationUrl = form.registrationUrl.trim();
    const replayUrl = form.replayUrl.trim();
    if ((registrationUrl && !/^https?:\/\//i.test(registrationUrl)) || (replayUrl && !/^https?:\/\//i.test(replayUrl))) {
      setError('Link must start with http:// or https://.');
      return;
    }
    if (status === 'published' && !form.backgroundImage.trim() && !form.heroImage.trim()) {
      setError('Add a background image before publishing.');
      return;
    }
    if (status === 'published' && isUpcoming && !form.date) {
      setError('Add a session date before publishing.');
      return;
    }
    if (status === 'published' && isUpcoming && !form.guestSpeaker.trim()) {
      setError('Add the guest speaker name before publishing — it appears on the event card art.');
      return;
    }
    const overLimitIndex = form.keyTakeaways.findIndex(isTakeawayOverLimit);
    if (overLimitIndex !== -1) {
      setError(`Takeaway ${overLimitIndex + 1} is over ${KEY_TAKEAWAY_MAX_CHARS} characters. Shorten the lead-in or the copy.`);
      return;
    }

    let startsAt = combineToUtc(form.date, form.time);
    if (!startsAt && !isUpcoming) {
      startsAt = moment().subtract(1, 'day').hour(12).minute(0).second(0).utc().toISOString();
    }

    // Preserve a hand-set endsAt across re-saves; if the start moved, keep the same session length.
    let endsAt = null;
    if (webinar?.endsAt) {
      const durationMs = webinar.startsAt ? moment(webinar.endsAt).diff(moment(webinar.startsAt)) : null;
      endsAt = durationMs && startsAt ? moment(startsAt).add(durationMs, 'ms').toISOString() : webinar.endsAt;
    }

    setSaving(true);
    setError(null);

    const guestSpeaker = form.guestSpeaker.trim();
    const headshot = form.headshot.trim();
    const existingSpeaker = webinar?.speakers?.[0] || {};

    // Link previews use the stored PNG, so it is redrawn whenever a field on the card changed.
    let cardArtImage = null;
    if (!cardMissingFields(zoomAssetInput).length) {
      const savedCard = { title: webinar?.title, speakerName: existingSpeaker.name, headshot: existingSpeaker.headshot, backgroundImage: webinar?.backgroundImage };
      const currentCard = { title: form.title.trim(), speakerName: guestSpeaker, headshot, backgroundImage: form.backgroundImage.trim() };
      cardArtImage = webinar?.cardArtImage && _.isEqual(savedCard, currentCard) ? webinar.cardArtImage : await uploadCardArt();
    }

    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      topic: form.topic.trim() || null,
      heroImage: form.heroImage.trim() || null,
      backgroundImage: form.backgroundImage.trim() || null,
      cardArtImage,
      startsAt,
      endsAt,
      ...(webinar?.publishedAt ? {} : { slug: form.slug.trim() || form.title.trim() }),
      registrationUrl: registrationUrl || null,
      replayUrl: replayUrl || null,
      replayKind: replayUrl ? form.replayKind : null,
      replayAssetId: webinar?.replayAssetId || null,
      replayPasscode: form.replayKind === 'external' ? form.replayPasscode.trim() || null : null,
      durationSeconds: parseDurationInput(form.replayDuration),
      speakers:
        guestSpeaker || headshot
          ? [
              {
                name: guestSpeaker,
                title: existingSpeaker.title || '',
                company: existingSpeaker.company || '',
                headshot,
                headshotSource: form.headshotSource.trim() || headshot,
                handle: form.handle.trim().replace(/^@+/, ''),
                tier: form.tier || ''
              }
            ]
          : [],
      audiences: webinar?.audiences || [],
      keyTakeaways: form.keyTakeaways.map(toSavedTakeaway).filter(Boolean),
      status,
      ...overrides
    };

    try {
      const saved = webinar?.id ? await updateWebinar(webinar.id, payload) : await createWebinar(payload);
      onSave(saved);
      // The editor stays mounted after a save on an existing row, so the flag must reset here.
      setSaving(false);
    } catch (err) {
      const backendMsg = err?.response?.data?.error || err?.response?.data?.msg;
      setError(backendMsg || 'Failed to save webinar. Please try again.');
      setSaving(false);
    }
  };

  const isLongSpeakerName = form.guestSpeaker.trim().length >= LONG_NAME_CHARS;

  return (
    <div className='wb-editor'>
      {error && <div className='wb-editor-error'>{error}</div>}

      <div className='wb-form-row'>
        <label>Event Name</label>
        <input type='text' value={form.title} onChange={e => setField('title', e.target.value)} placeholder='Title here' />
      </div>

      <div className='wb-form-row'>
        <label>Page URL</label>
        <input
          type='text'
          value={form.slug}
          onChange={e => setField('slug', e.target.value)}
          placeholder='Generated from the event name if left blank'
          disabled={!!webinar?.publishedAt}
        />
        <div className='wb-form-helper'>
          {webinar?.publishedAt
            ? 'The URL is frozen once a webinar has been published — the link may already be shared or indexed.'
            : `After you publish: shopmy.us/home/creators/webinars/${form.slug.trim() || '{generated-from-name}'}`}
        </div>
      </div>

      <div className='wb-form-row'>
        <label>Guest Speaker Name</label>
        <input
          type='text'
          value={form.guestSpeaker}
          onChange={e => setField('guestSpeaker', e.target.value)}
          placeholder='e.g. Coco Schiffer'
        />
        <div className='wb-form-helper'>
          Guest Speaker is always on the overview image. The name is inserted under it.
          {isLongSpeakerName
            ? ' This name is long — skip the @ and tier so the photo looks right, like Jackie & Julia.'
            : ''}
        </div>
      </div>

      <div className='wb-form-row wb-form-row--split'>
        <div>
          <label>@ Handle</label>
          <input type='text' value={form.handle} onChange={e => setField('handle', e.target.value)} placeholder='e.g. everydayparisian' />
          <div className='wb-form-helper'>Optional. Shown under the name as @handle. Leave blank for long names.</div>
        </div>
        <div>
          <label>Tier</label>
          <select value={form.tier} onChange={e => setField('tier', e.target.value)}>
            <option value=''>None</option>
            {SPEAKER_TIERS.map(tier => (
              <option key={tier} value={tier}>
                {tier}
              </option>
            ))}
          </select>
          <div className='wb-form-helper'>Optional. Icon, Trendsetter, Ambassador, or Enthusiast.</div>
        </div>
      </div>

      <div className='wb-form-row'>
        <label>Event Overview</label>
        <textarea
          value={form.description}
          onChange={e => setField('description', e.target.value)}
          placeholder='Rebecca Plotnick joins us live on September 23rd. She will walk through how a decade of storytelling became a business, and how consistently linking her favorite brands turned into real partnerships.'
          rows={4}
        />
        <div className='wb-form-helper'>
          {form.description.trim().length}/{EVENT_OVERVIEW_MAX_CHARS} characters. This copy is the Zoom Event Overview and the public card description. Stay around two to three sentences.
        </div>
      </div>

      {hasBeenPublished && (
        <div className='wb-form-row'>
          <label>Visibility</label>
          <select value={visibility} onChange={e => setVisibility(e.target.value)}>
            <option value='published'>Published — visible on the public page</option>
            <option value='hidden'>Hidden — removed from the public page, URL stays reserved</option>
            <option value='archived'>Archived — stays in the library, out of active rotation</option>
          </select>
          <div className='wb-form-helper'>Use Hidden to take a webinar down immediately without deleting it.</div>
        </div>
      )}

      {isUpcoming ? (
        <>
          <div className='wb-form-row wb-form-row--split'>
            <div>
              <label>Session Starts</label>
              <input type='date' value={form.date} onChange={e => setField('date', e.target.value)} />
            </div>
            <div>
              <label>Time</label>
              <input type='time' value={form.time} onChange={e => setField('time', e.target.value)} />
            </div>
          </div>
          <div className='wb-form-row'>
            <label className='wb-visually-hidden'>Timezone</label>
            <input type='text' value={getEasternOffsetLabel(form.date)} disabled />
            <div className='wb-form-helper'>Times are entered in Eastern Time and shown to visitors as ET.</div>
          </div>
        </>
      ) : (
        <>
          <div className='wb-form-row wb-form-row--split'>
            <div>
              <label>Original Session Date</label>
              <input type='date' value={form.date} onChange={e => setField('date', e.target.value)} />
            </div>
            <div>
              <label>Time</label>
              <input type='time' value={form.time} onChange={e => setField('time', e.target.value)} />
            </div>
          </div>
          <div className='wb-form-row'>
            <label className='wb-visually-hidden'>Timezone</label>
            <input type='text' value={getEasternOffsetLabel(form.date)} disabled />
            <div className='wb-form-helper'>Times are entered in Eastern Time and shown to visitors as ET.</div>
          </div>
        </>
      )}

      {isUpcoming && (
        <div className='wb-form-row'>
          <label>Webinar Zoom Registration Link</label>
          <input
            type='url'
            value={form.registrationUrl}
            onChange={e => setField('registrationUrl', e.target.value)}
            placeholder='Link URL'
          />
          <div className='wb-form-helper'>Until this is set, the public card shows "Registration opens soon" instead of a Register Now button.</div>
        </div>
      )}

      <div className='wb-form-row wb-form-row--split'>
        <div>
          <label>Webinar Recording Link</label>
          <input type='url' value={form.replayUrl} onChange={e => setField('replayUrl', e.target.value)} placeholder='Link URL' />
          <div className='wb-form-helper'>
            Watch Now points here. Once the session has aired, it stays hidden from the public library until this is set.
          </div>
        </div>
        <div>
          <label>Duration (Optional)</label>
          <input type='text' value={form.replayDuration} onChange={e => setField('replayDuration', e.target.value)} placeholder='e.g. 58:12' />
        </div>
      </div>

      {form.replayUrl.trim() && (
        <div className='wb-form-row wb-form-row--split'>
          <div>
            <label>Recording Type</label>
            <select value={form.replayKind} onChange={e => setField('replayKind', e.target.value)}>
              <option value='hosted'>Hosted video (plays on our replay page)</option>
              <option value='external'>External link-out (e.g. a Zoom recording page)</option>
            </select>
          </div>
          {form.replayKind === 'external' && (
            <div>
              <label>Passcode (Optional)</label>
              <input
                type='text'
                value={form.replayPasscode}
                onChange={e => setField('replayPasscode', e.target.value)}
                placeholder='Shown next to the Watch on Zoom link'
              />
            </div>
          )}
        </div>
      )}

      <div className='wb-form-row'>
        <label>Stock background</label>
        <div className='wb-form-helper'>Pick a stock background first. It fills the card behind the headshot.</div>
        <div className='wb-stock-grid'>
          {WEBINAR_STOCK_BACKGROUNDS.map(stock => {
            const missing = !!missingStockIds[stock.id];
            const selected = form.backgroundImage === stock.url;
            return (
              <div
                key={stock.id}
                className={`wb-stock-tile${selected ? ' wb-stock-tile--selected' : ''}${missing ? ' wb-stock-tile--empty' : ''}`}
              >
                <button type='button' className='wb-stock-tile-pick' onClick={() => setField('backgroundImage', stock.url)}>
                  {missing ? (
                    <span>{stock.id}</span>
                  ) : (
                    <ShopmyImage
                      source={stock.url}
                      alt={`Stock background ${stock.id}`}
                      onError={() => setMissingStockIds(prev => ({ ...prev, [stock.id]: true }))}
                    />
                  )}
                  {selected && (
                    <span className='wb-stock-tile-check'>
                      <FontAwesomeIcon icon={faCheck} />
                    </span>
                  )}
                </button>
                {!missing && (
                  <button
                    type='button'
                    className='wb-stock-tile-download'
                    onClick={e => downloadStockBackground(e, stock)}
                    title='Download background'
                  >
                    <FontAwesomeIcon icon={faDownload} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className='wb-cover-split'>
        {IMAGE_SLOTS.map(({ field, title, helper, sizeHint }) => (
          <div key={field} className='wb-cover' onClick={() => !form[field] && setImageUploaderField(field)}>
            {form[field] ? (
              <>
                <ShopmyImage source={form[field]} alt={title} className='wb-cover-preview' />
                <div className='wb-cover-actions'>
                  <button type='button' className='wb-btn wb-btn--secondary' onClick={() => setImageUploaderField(field)}>
                    Change Image
                  </button>
                  <button type='button' className='wb-btn wb-btn--ghost' onClick={() => setImageField(field, '', '')}>
                    Remove
                  </button>
                </div>
                <div className='wb-cover-helper'>{sizeHint}</div>
              </>
            ) : (
              <>
                <div className='wb-cover-title'>{title}</div>
                <div className='wb-cover-helper'>{helper}</div>
                <div className='wb-cover-helper'>{sizeHint}</div>
                <button
                  type='button'
                  className='wb-btn wb-btn--secondary'
                  onClick={e => {
                    e.stopPropagation();
                    setImageUploaderField(field);
                  }}
                >
                  Select Files
                </button>
              </>
            )}
          </div>
        ))}
      </div>

      <div className='wb-takeaways'>
        <label>Key Takeaways</label>
        <div className='wb-form-helper'>
          Up to four short lines for Zoom and the replay page. The lead-in is set bold. Keep each line to about two rows — 120 characters is the hard stop so the image stays readable.
        </div>
        {takeaways.map((item, index) => (
          <div key={index} className='wb-form-row'>
            <div className='wb-takeaway-line-head'>
              <label>Takeaway {index + 1}</label>
              <button type='button' className='wb-btn wb-btn--ghost' onClick={() => removeTakeaway(index)}>
                Remove
              </button>
            </div>
            <div className='wb-takeaway-fields'>
              <input
                type='text'
                value={item.lead}
                maxLength={KEY_TAKEAWAY_LEAD_MAX_CHARS}
                onChange={e => setTakeawayField(index, 'lead', e.target.value)}
                placeholder='Bold lead-in (optional)'
              />
              <input
                type='text'
                value={item.copy}
                maxLength={KEY_TAKEAWAY_MAX_CHARS - item.lead.length}
                onChange={e => setTakeawayField(index, 'copy', e.target.value)}
                placeholder='One short takeaway'
              />
            </div>
            <div className={`wb-form-helper${isTakeawayOverLimit(item) ? ' wb-form-helper--over' : ''}`}>
              {item.lead.length + item.copy.length}/{KEY_TAKEAWAY_MAX_CHARS} characters
            </div>
          </div>
        ))}
        {takeaways.length < KEY_TAKEAWAY_MAX && (
          <button type='button' className='wb-btn wb-btn--secondary' onClick={addTakeaway}>
            + Add Takeaway ({takeaways.length}/{KEY_TAKEAWAY_MAX})
          </button>
        )}
      </div>

      <div className='wb-form-row'>
        <label>Zoom images</label>
        <div className='wb-form-helper'>Drawn live from the fields above. Download each PNG and upload it to the Zoom event page.</div>
        <WebinarZoomAssets webinar={zoomAssetInput} />
      </div>

      <div className='wb-form-row'>
        <label>Tags</label>
        <div className='wb-tags'>
          {isUpcoming && (
            <span className='wb-tag'>
              Live Webinar
            </span>
          )}
          {form.topic && (
            <span className='wb-tag'>
              {form.topic}
              <button type='button' className='wb-tag-remove' onClick={() => setField('topic', '')} title='Remove tag'>
                ×
              </button>
            </span>
          )}
          <div className='wb-tags-select'>
            <CreatableSelect
              isClearable
              options={topicOptions}
              value={null}
              onChange={option => setField('topic', option?.value || '')}
              onCreateOption={value => {
                setTopicOptions(prev => [...prev, { value, label: value }]);
                setField('topic', value);
              }}
              formatCreateLabel={value => `Create new tag "${value}"`}
              placeholder={form.topic ? 'Replace the topic tag...' : 'Select or type to create a tag...'}
              classNamePrefix='wb-react-select'
            />
          </div>
        </div>
        <div className='wb-form-helper'>One topic tag per webinar — pick an existing tag or type a new name to create one.</div>
      </div>

      <div className='wb-editor-footer'>
        {webinar?.id && onDelete && (
          <button type='button' className='wb-btn wb-btn--danger-text' onClick={() => onDelete(webinar)} disabled={saving}>
            Delete
          </button>
        )}
        <div className='wb-editor-footer-actions'>
          <button type='button' className='wb-btn wb-btn--ghost' onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          {!hasBeenPublished && (
            <button type='button' className='wb-btn wb-btn--secondary' onClick={() => save('draft')} disabled={saving}>
              {saving ? 'Saving...' : 'Save as Draft'}
            </button>
          )}
          <button type='button' className='wb-btn wb-btn--primary' onClick={() => save(hasBeenPublished ? visibility : 'published')} disabled={saving}>
            {saving ? 'Saving...' : hasBeenPublished ? 'Save Changes' : 'Publish'}
          </button>
        </div>
      </div>

      {imageUploaderField && (
        <ImageUploader
          isVisible
          setIsVisible={visible => !visible && setImageUploaderField(null)}
          initialImageUrl={form[imageUploaderField] || ''}
          initialSourceUrl={form[`${imageUploaderField}Source`] || ''}
          onSaveCallback={(url, { sourceUrl } = {}) => setImageField(imageUploaderField, url, sourceUrl)}
          allowCrop
          cropAspect={1}
          cropSize={HEADSHOT_CROP_SIZE}
        />
      )}
    </div>
  );
};

export default WebinarEditor;
